import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import FormTemplate from "../../../forms/FormTemplate";
import SelectArrow from "../../../styles/icons/SelectArrow";

const FIELD_TYPE_OPTIONS = [
  { value: "string", label: "Text" },
  { value: "int", label: "Integer" },
  { value: "bool", label: "Boolean" },
  { value: "date", label: "Date" },
];

const createRowId = () =>
  `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const emptyField = () => ({
  rowId: createRowId(),
  key: "",
  label: "",
  type: "string",
  required: false,
});

const normalizeKey = (value) => (value || "").trim();

export default function ExternalApiTriggerSideMenu({
  selectedNode,
  onClose,
  onSubmit,
}) {
  const [description, setDescription] = useState(
    (selectedNode?.data?.description || "").trim()
  );
  const [externalFields, setExternalFields] = useState(() => {
    const existing = Array.isArray(selectedNode?.data?.externalFields)
      ? selectedNode.data.externalFields
      : [];

    if (!existing.length) return [];

    return existing.map((field) => ({
      rowId: createRowId(),
      key: normalizeKey(field?.key || field?.name || ""),
      label: (field?.label || "").trim(),
      type: (field?.type || "string").toLowerCase(),
      required: Boolean(field?.required),
    }));
  });

  const errors = useMemo(() => {
    const list = [];
    const seen = new Set();
    const keyPattern = /^[A-Za-z][A-Za-z0-9_]*$/;

    externalFields.forEach((field, index) => {
      const normalizedKey = normalizeKey(field.key);

      if (!normalizedKey) {
        list.push(`Row ${index + 1}: Key is required.`);
        return;
      }

      if (!keyPattern.test(normalizedKey)) {
        list.push(
          `Row ${index + 1}: Key must start with a letter and contain only letters, numbers, or underscore.`
        );
      }

      const lowered = normalizedKey.toLowerCase();
      if (seen.has(lowered)) {
        list.push(`Row ${index + 1}: Duplicate key '${normalizedKey}'.`);
      } else {
        seen.add(lowered);
      }
    });

    return list;
  }, [externalFields]);

  const isValid = errors.length === 0;
  const updateField = (index, patch) => {
    setExternalFields((prev) =>
      prev.map((field, i) => (i === index ? { ...field, ...patch } : field))
    );
  };

  const addField = () => {
    setExternalFields((prev) => [...prev, emptyField()]);
  };

  const removeField = (index) => {
    setExternalFields((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (!isValid) return;

    const sanitizedFields = externalFields.map((field) => ({
      key: normalizeKey(field.key),
      label: (field.label || "").trim(),
      type: (field.type || "string").toLowerCase(),
      required: Boolean(field.required),
    }));

    onSubmit({
      label: selectedNode?.data?.label || "External API",
      description: description.trim(),
      externalFields: sanitizedFields,
    });
    onClose();
  };

  return (
    <FormTemplate
      title={selectedNode ? "Edit External API" : "External API"}
      isValid={isValid}
      onClose={onClose}
      handleSubmit={handleSave}
      extraLeftAction={
        <Button
          onClick={addField}
          variant="outlined"
          startIcon={<AddIcon />}
          sx={{
            borderColor: "#0DA3E2",
            color: "#FFFFFF",
            textTransform: "none",
            "&:hover": {
              borderColor: "#0DA3E2",
              bgcolor: "rgba(13, 163, 226, 0.08)",
            },
          }}
        >
          Add External Field
        </Button>
      }
    >
      <Box sx={{ width: "100%", minWidth: 780 }}>
        <Typography sx={{ mb: 2, color: "rgba(255,255,255,0.8)" }}>
          Configure fields that external systems can send per lead when
          triggering this flow.
        </Typography>

        <Typography sx={{ mb: 2, color: "rgba(255,255,255,0.72)" }}>
          Need request examples? Click the info icon on the node header.
        </Typography>

        <TextField
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe the purpose of this external trigger"
          size="small"
          fullWidth
          sx={{ mb: 2 }}
        />

        {externalFields.length === 0 && (
          <Typography sx={{ color: "rgba(255,255,255,0.75)" }}>
            No external fields configured. You can still trigger by lead ID
            only.
          </Typography>
        )}

        <Stack spacing={1.5}>
          {externalFields.map((field, index) => (
            <Stack
              key={field.rowId}
              direction="row"
              spacing={1.5}
              alignItems="center"
            >
              <TextField
                label="Key"
                value={field.key}
                onChange={(e) => updateField(index, { key: e.target.value })}
                size="small"
                sx={{ minWidth: 200 }}
              />

              <TextField
                label="Label (optional)"
                value={field.label}
                onChange={(e) => updateField(index, { label: e.target.value })}
                size="small"
                sx={{ minWidth: 220 }}
              />

              <Select
                value={field.type}
                onChange={(e) => updateField(index, { type: e.target.value })}
                size="small"
                IconComponent={SelectArrow}
                sx={{ minWidth: 140 }}
              >
                {FIELD_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>

              <FormControlLabel
                sx={{ ml: 1 }}
                control={
                  <Checkbox
                    checked={Boolean(field.required)}
                    onChange={(e) =>
                      updateField(index, { required: e.target.checked })
                    }
                  />
                }
                label="Required"
              />

              <IconButton onClick={() => removeField(index)} size="small">
                <DeleteIcon sx={{ color: "#aaa" }} />
              </IconButton>
            </Stack>
          ))}
        </Stack>

        {errors.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {errors.map((err) => (
              <Typography key={err} color="#EB4D4D">
                {err}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    </FormTemplate>
  );
}
